import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import type { AdDurationOption, MaxAdDurationStatePayload } from '@openx/types/targeting/video';
import type { HTMLAttributes, JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidation } from '../hooks/useValidation';
import { maxLengthOptions, minLengthOptions } from '../utils/constants';

type Props = {
  renderOption: (props: HTMLAttributes<HTMLLIElement>, option: AdDurationOption) => ReactNode;
  onChange: (typeParams: Partial<MaxAdDurationStatePayload> | null) => void;
  value: MaxAdDurationStatePayload | null;
};

export const BetweenItem = ({ renderOption, onChange, value }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { validation } = useValidation(value);

  return (
    <>
      <Grid size={9}>
        <AutoComplete<AdDurationOption, false, boolean, boolean>
          options={minLengthOptions}
          onChange={(_e, newValue) => onChange({ start: newValue })}
          value={value?.start ?? null}
          onInputChange={(_e, newValue) => onChange({ start: newValue })}
          inputValue={value?.start ?? ''}
          fullWidth
          textFieldProps={{
            placeholder: '1',
            slotProps: {
              input: {
                endAdornment: <Typography color="textSecondary">{t('sec')}</Typography>,
              },
            },
            required: true,
            error: validation.start.error,
            helperText: validation.start.helperText,
          }}
          renderOption={renderOption}
          disableClearable
          freeSolo
          forcePopupIcon
          data-test="between-item-min-input"
        />
      </Grid>

      <Grid size={1} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', mt: 0.5 }}>
        <Typography color="textSecondary">
          <HorizontalRuleIcon sx={{ fontSize: '0.75rem' }} />
        </Typography>
      </Grid>

      <Grid size={9}>
        <AutoComplete<AdDurationOption, false, boolean, boolean>
          options={maxLengthOptions}
          onChange={(_e, newValue) => onChange({ end: newValue })}
          value={value?.end ?? null}
          onInputChange={(_e, newValue) => onChange({ end: newValue })}
          inputValue={value?.end ?? ''}
          fullWidth
          textFieldProps={{
            placeholder: '120',
            slotProps: {
              input: {
                endAdornment: <Typography color="textSecondary">{t('sec')}</Typography>,
              },
            },
            error: validation.end.error,
            helperText: validation.end.helperText,
          }}
          renderOption={renderOption}
          disableClearable
          freeSolo
          forcePopupIcon
          data-test="between-item-max-input"
        />
      </Grid>
    </>
  );
};
